import { JSX, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { EntityTypeEnum, ProjectRunConfigurationType, RegionSetStatusType } from "@app/projects/enumerators";
import AvailableRegionStatusesModel from "@app/projects/availableRegionStatusesModel";
import ProjectService from "@app/projects/projectService";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { getLastAppliedStatusFilters, NetworkIntegrityContext, setLastAppliedDisplayMode, setLastAppliedMembership, setLastAppliedProviderType, setLastAppliedStatusFilters } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityProviderTypeFilter from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityProviderTypeFilter";
import { NetworkIntegrityActionStatus, NetworkIntegrityDisplayMode, NetworkIntegrityMembership, NetworkIntegrityProviderTypes } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import NetworkIntegrityMembershipFilter from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityMembershipFilter";
import NetworkIntegrityResultsAsOfFilter from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityResultsAsOfFilter";
import NetworkIntegrityServiceAreaFilter from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityServiceAreaFilter";
import NetworkIntegritySpecialtyFilter from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegritySpecialtyFilter";
import NetworkIntegrityActionStatusFilter from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityActionStatusFilter";
import NetworkIntegrityNpiTypeFilter from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityNpiTypeFilter";
import { NetworkIntegrityAppliedFilters } from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityAppliedFilters";
import { NetworkIntegrityFilterAction } from "@app/projects/network-assessment/network-integrity/state-management/networkIntegrityFilterStateManagement";
import { NetworkIntegrityJobAction } from "@app/projects/network-assessment/network-integrity/state-management/networkIntegrityJobStateManagement";
import { NetworkIntegrityRouteState, RouteMatchParams } from "@app/projects/network-assessment/network-integrity/models";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { ServiceAreaNestingParentDropdownItem } from "@app/report-customization/manage-presets/interfaces/serviceAreaNestingParentDropdownItem";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { SpecialtyGroupWithSpecialtiesModel } from "@app/shared/interfaces/specialtyGroupWithSpecialtiesModel";
import { selectedKeysToSpecialtyCodes, specialtyCodesToSelectedKeys, specialtyGroupsToNestedItems, specialtyLabel, tryGetSingleFullyCoveredGroupCode, tryGetSingleFullyCoveredGroupCodeFromKeys } from "@app/projects/network-assessment/network-integrity/helpers/specialtyKeyHelpers";
import { isHistoricalRun } from "@app/projects/network-assessment/network-integrity/helpers/historicalRunHelper";
import { isNpiTypeEnabled } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityNpiTypeHelper";
import { getResultsAsOfOptions } from "@app/projects/network-assessment/network-integrity/helpers/resultsAsOfHelper";
import { ZipProjectContext } from "@app/projects/zip-projects/ZipProjectContext";
import { getSelectionText } from "@app/shared/utility/getSelectionText";
import { QESButton } from "@app/shared/qes-standards/Button";
import InfoIndicator from "@app/shared/InfoIndicator";
import RegionFilters from "@app/projects/regionFilters";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

// Empty by default; defaultPillExpansion logic remains in case requirements change.
const NI_DEFAULT_STATUS_FILTERS: readonly RegionSetStatusType[] = [];

// Default View — Servicing (matches networkIntegrityFilterInitialState).
const NI_DEFAULT_PROVIDER_TYPE: string = NetworkIntegrityProviderTypes.Servicing;

const NI_STATUS_DISPLAY_ORDER: readonly RegionSetStatusType[] = [
  RegionSetStatusType.Active,
  RegionSetStatusType.Pending,
  RegionSetStatusType.Approved,
  RegionSetStatusType.Opportunity,
];

const setsEqual = <T,>(a: Set<T>, b: Set<T>): boolean => {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
};

interface NetworkIntegrityFilterContainerProps {
  isProviderDetailPage?: boolean;
  isGapDetailPage?: boolean;
  isViewAllPage?: boolean;
  isExcessWarningsBreakdownPage?: boolean;
  // Fallback run config id from a host that doesn't expose CountyProjectContext (Zip projects supply it from
  // ZipProjectContext). Used only when location.state, CountyProjectContext, and the URL all lack one.
  projectRunConfigurationId?: number;
}

const NetworkIntegrityFilterContainer = ({ isProviderDetailPage = false, isGapDetailPage = false, isViewAllPage = false, isExcessWarningsBreakdownPage = false, projectRunConfigurationId: hostProjectRunConfigurationId }: NetworkIntegrityFilterContainerProps = {}): JSX.Element => {
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const projectContext = useContext(CountyProjectContext);
  const zipProjectContext = useContext(ZipProjectContext);
  const user = useContext(UserContext);
  const ffContext = useContext(FeatureFlagContext);
  const isDashboard = !isProviderDetailPage && !isGapDetailPage && !isViewAllPage;
  const isTermAdmin = user.roles.includes(Roles.TERM_PROVIDER_ADMIN);
  const isTermViewer = user.roles.includes(Roles.TERM_PROVIDER_VIEWER);
  const isNetworkProviderType = (pt: string): boolean =>
    pt !== NetworkIntegrityProviderTypes.Prospect &&
    pt !== NetworkIntegrityProviderTypes.ProspectServicing
  const showTermedStatus = ffContext.enabledFeatureFlags.has(Features.DISPLAY_NETWORK_INTEGRITY_TOTAL_AFTER_TERMED);
  const isHistoricalRunView = isHistoricalRun(networkIntegrityContext.projectRunConfiguration);
  const showActionStatusFilter = showTermedStatus && isProviderDetailPage && (isTermAdmin || isTermViewer) && !isHistoricalRunView;
  const showDisplayToggle = showTermedStatus && (isTermAdmin || isTermViewer) && isNetworkProviderType(networkIntegrityContext.providerType ?? "");
  const location = useLocation<NetworkIntegrityRouteState>();
  const routeMatch = useRouteMatch<RouteMatchParams>();
  const showNpiTypeFilter = (isProviderDetailPage || isExcessWarningsBreakdownPage) && isNpiTypeEnabled(ffContext.enabledFeatureFlags, routeMatch?.params?.aggregateGroup);
  const history = useHistory();
  const replaceLocationState = (partialState: Record<string, unknown>): void => {
    history.replace({
      ...history.location,
      state: { ...(history.location.state as Record<string, unknown>), ...partialState },
    });
  };
  const projectService = useMemo(() => new ProjectService(), []);
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);
  const originalRegionSetIds = useRef<number[]>([]);
  const originalSpecialtyCodes = useRef<string[]>([]);
  const appliedProviderType = useRef<string>(null);
  const appliedMembership = networkIntegrityContext.membership ?? NetworkIntegrityMembership.Primary;
  const aliasMaxLength = 25;

  const [hasDualMembershipProviders, setHasDualMembershipProviders] = useState<boolean>(false);
  const [hasProspectProviders, setHasProspectProviders] = useState<boolean>(false)
  const [isDualPrimaryMembershipSelected, setIsDualPrimaryMembershipSelected] = useState<boolean>(true);
  const [primaryMemberAlias, setPrimaryMemberAlias] = useState<string>(null);
  const [secondaryMemberAlias, setSecondaryMemberAlias] = useState<string>(null);
  const [primaryRunConfiguration, setPrimaryRunConfiguration] = useState<ProjectRunConfiguration>(null);
  const [companionRunConfiguration, setCompanionRunConfiguration] = useState<ProjectRunConfiguration>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [serviceAreaOptions, setServiceAreaOptions] = useState<ServiceAreaNestingParentDropdownItem[]>([]);
  const [specialtyGroups, setSpecialtyGroups] = useState<SpecialtyGroupWithSpecialtiesModel[]>([]);
  const specialtyOptions = useMemo<NestingParentDropdownItem[]>(
    () => specialtyGroupsToNestedItems(specialtyGroups),
    [specialtyGroups],
  );
  const canSeeUnpublishedRuns = user.roles.includes(Roles.PREVIEW) || user.roles.includes(Roles.QUEST_ADMIN);
  const resultsAsOfOptions = useMemo(
    () => getResultsAsOfOptions(networkIntegrityContext.project, canSeeUnpublishedRuns),
    [networkIntegrityContext.project, canSeeUnpublishedRuns],
  );

  // Set only on Apply. The load effect keys off it so a timeslice change re-reads the run's dual-membership pair,
  // service areas, specialties and statuses; the effect's own context dispatches don't touch it, so it can't loop.
  const [appliedResultsAsOfId, setAppliedResultsAsOfId] = useState<string>(null);
  const [pendingResultsAsOfId, setPendingResultsAsOfId] = useState<string>(null);
  const [pendingProviderType, setPendingProviderType] = useState<string>(null);
  const [pendingRegionSetIds, setPendingRegionSetIds] = useState<number[]>([]);
  // Holds groupCode::specialtyCode keys  so a specialty appearing under
  // multiple groups can be selected under one group.
  const [pendingSpecialtyCodes, setPendingSpecialtyCodes] = useState<string[]>([]);
  const specialtySelectedKeys = pendingSpecialtyCodes;
  const [pendingActionStatus, setPendingActionStatus] = useState<NetworkIntegrityActionStatus>(NetworkIntegrityActionStatus.All);
  const [pendingMembership, setPendingMembership] = useState<NetworkIntegrityMembership>(NetworkIntegrityMembership.Primary);
  const [pendingNpiType, setPendingNpiType] = useState<EntityTypeEnum | null>(null);
  const [statusToRegionSet, setStatusToRegionSet] = useState<Map<RegionSetStatusType, Set<number>>>(new Map());
  const initialStatusFilters = getLastAppliedStatusFilters() ?? NI_DEFAULT_STATUS_FILTERS;
  const [activeServiceAreaStatusFilters, setActiveServiceAreaStatusFilters] = useState<Set<RegionSetStatusType>>(
    new Set(initialStatusFilters)
  );
  const excessWarning = useRef<string>(location.state?.initialExcessWarning ?? null);
  const appliedServiceAreaStatusFilters = useRef<Set<RegionSetStatusType>>(new Set(initialStatusFilters));

  const projectId = projectContext?.project?.projectId ?? routeMatch?.params?.projectId;
  const locationRunConfigId = location.state?.runConfiguration?.projectRunConfigurationId;
  // An applied "Results as of:" selection outranks every other source, including location state, which would
  // otherwise keep pointing at the run the user arrived on.
  const projectRunConfigurationId = appliedResultsAsOfId ?? locationRunConfigId ?? projectContext?.runConfiguration?.projectRunConfigurationId ?? routeMatch?.params?.projectRunConfigurationId ?? hostProjectRunConfigurationId?.toString();
  const routeProviderType = routeMatch?.params?.providerType ?? location.state?.initialProviderType ?? networkIntegrityContext.providerType;

  // Only show the toggle when the run config has processed terminations (else the after-termed view equals Current).
  const displayToggleEligible = showDisplayToggle && !(isProviderDetailPage || isGapDetailPage) && !isHistoricalRunView;
  const { data: hasProcessedTerminations = false, isLoading: hasProcessedTerminationsLoading } = useQuery({
    enabled: displayToggleEligible && !!projectId && !!projectRunConfigurationId,
    queryFn: ({ signal }) => networkIntegrityService.getHasProcessedTerminations(Number(projectId), String(projectRunConfigurationId), signal),
    queryKey: ["ni-has-processed-terminations", projectId, projectRunConfigurationId],
  });

  // displayMode persists across projects; reset to Current when the toggle isn't shown so it can't strand here.
  // Skip while the query is loading so a project that has terminations isn't reset before it resolves.
  useEffect(() => {
    if (!hasProcessedTerminationsLoading
      && !(displayToggleEligible && hasProcessedTerminations)
      && networkIntegrityContext.displayMode === NetworkIntegrityDisplayMode.TotalAfterTermed) {
      networkIntegrityContext.filtersChanged({
        type: NetworkIntegrityFilterAction.DisplayModeChanged,
        value: { displayMode: NetworkIntegrityDisplayMode.Current }
      });
    }
  }, [displayToggleEligible, hasProcessedTerminations, hasProcessedTerminationsLoading, networkIntegrityContext.displayMode, networkIntegrityContext.filtersChanged]);

  // An action status can arrive by route state from an after-termed link. The filter is hidden on a historical run,
  // so clear both the applied and the pending value: the load effect below re-seeds pendingActionStatus from the
  // context it captured, so clearing only the applied value would leave a pending one that Apply could send again.
  // pendingActionStatus is a dependency so this re-runs after that re-seed; both writes are guarded, so it settles.
  useEffect(() => {
    if (!isHistoricalRunView) return;

    if (pendingActionStatus !== NetworkIntegrityActionStatus.All)
      setPendingActionStatus(NetworkIntegrityActionStatus.All);

    if (networkIntegrityContext.actionStatus !== NetworkIntegrityActionStatus.All) {
      networkIntegrityContext.filtersChanged({
        type: NetworkIntegrityFilterAction.ActionStatusFilterChanged,
        value: { actionStatus: NetworkIntegrityActionStatus.All }
      });
    }
  }, [isHistoricalRunView, pendingActionStatus, networkIntegrityContext.actionStatus, networkIntegrityContext.filtersChanged]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      const project = await projectService.getById(Number(projectId), true);
      if (cancelled) return;

      const runConfig = project.projectRunConfigurations?.find(
        (rc) => rc.projectRunConfigurationId === Number(projectRunConfigurationId)
      );

      const companionProjectRunConfigurationId = runConfig?.companionProjectRunConfigurationId;
      let computedMembership: NetworkIntegrityMembership = NetworkIntegrityMembership.Primary;
      // The dropdown lists primary configs only, so a secondary-membership view still shows its pair's primary.
      let timesliceOptionId: string = runConfig?.projectRunConfigurationId?.toString();
      if (companionProjectRunConfigurationId != null) {
        const companionRunConfig = project.projectRunConfigurations?.find(
          (rc) => rc.projectRunConfigurationId === Number(companionProjectRunConfigurationId)
        );

        const primaryConfig = runConfig?.projectRunConfigurationType == ProjectRunConfigurationType.DualMembershipPrimary ? runConfig : companionRunConfig;
        const companionConfig = runConfig?.projectRunConfigurationType == ProjectRunConfigurationType.DualMembershipSupplemental ? runConfig : companionRunConfig;

        setHasDualMembershipProviders(true);
        setHasProspectProviders(!isGapDetailPage && primaryConfig?.prospectProviderSetId != null);
        setPrimaryRunConfiguration(primaryConfig ?? null);
        setCompanionRunConfiguration(companionConfig ?? null);
        timesliceOptionId = primaryConfig?.projectRunConfigurationId?.toString() ?? timesliceOptionId;

        const primaryAlias = truncateIfTooLong(primaryConfig?.memberSetAlias, aliasMaxLength);
        const companionAlias = truncateIfTooLong(companionConfig?.memberSetAlias, aliasMaxLength);

        setPrimaryMemberAlias(primaryAlias);
        setSecondaryMemberAlias(companionAlias);

        const isServicingProviderType = !routeProviderType
          || routeProviderType === NetworkIntegrityProviderTypes.Servicing
          || routeProviderType === NetworkIntegrityProviderTypes.ProspectServicing;

        if (isServicingProviderType) {
          if (runConfig?.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipPrimary) {
            setIsDualPrimaryMembershipSelected(false);
            computedMembership = NetworkIntegrityMembership.Secondary;
            networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationDualTypeChanged, value: { projectRunConfigurationDualType: ProjectRunConfigurationType.DualMembershipSupplemental } });
            networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationChanged, value: { projectRunConfiguration: companionConfig } });
            networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationIdChanged, value: { projectRunConfigurationId: companionConfig.projectRunConfigurationId.toString() } });
          }
          else {
            setIsDualPrimaryMembershipSelected(true);
            networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationChanged, value: { projectRunConfiguration: primaryConfig } });
            networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationIdChanged, value: { projectRunConfigurationId: primaryConfig.projectRunConfigurationId.toString() } });
          }
        }

        if (projectContext?.runConfiguration?.projectRunConfigurationId && !locationRunConfigId) {
          const targetConfig = computedMembership === NetworkIntegrityMembership.Secondary ? companionConfig : primaryConfig;
          networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationIdChanged, value: { projectRunConfigurationId: targetConfig.projectRunConfigurationId.toString() } });
          networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationChanged, value: {projectRunConfiguration: targetConfig}});
        }

        // Skipped once the user has applied a timeslice — location state still names the run they arrived on.
        if (locationRunConfigId && !appliedResultsAsOfId) {
          const locationConfig = Number(locationRunConfigId) === primaryConfig.projectRunConfigurationId ? primaryConfig : companionConfig;
          computedMembership = locationConfig.projectRunConfigurationId === companionConfig?.projectRunConfigurationId
            ? NetworkIntegrityMembership.Secondary
            : NetworkIntegrityMembership.Primary;
          networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationIdChanged, value: { projectRunConfigurationId: locationConfig.projectRunConfigurationId.toString() } });
          networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationChanged, value: { projectRunConfiguration: locationConfig } });
        }
      }
      else {
        setPrimaryRunConfiguration(runConfig);
        setHasProspectProviders(!isGapDetailPage && runConfig?.prospectProviderSetId != null);
        // The effect re-runs when the timeslice changes, so the dual-membership state has to be cleared here too:
        // hasDualMembershipProviders is only ever set true, and a stale companion belongs to the previous run.
        setHasDualMembershipProviders(false);
        setCompanionRunConfiguration(null);
        setPrimaryMemberAlias(null);
        setSecondaryMemberAlias(null);
        setIsDualPrimaryMembershipSelected(true);
        networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationIdChanged, value: { projectRunConfigurationId: runConfig.projectRunConfigurationId.toString() } });
        networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationChanged, value: { projectRunConfiguration: runConfig } });
      }

      const normalizedRouteProviderType = routeProviderType;
      if (normalizedRouteProviderType && normalizedRouteProviderType !== networkIntegrityContext.providerType) {
        networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProviderTypeFilterChanged, value: { providerType: normalizedRouteProviderType } });
      }

      const [specialties, counties, availableStatuses] = await Promise.all([
        projectService.getSpecialtyGroupsWithSpecialtiesByRunConfigurationId(projectId.toString(), projectRunConfigurationId.toString()),
        projectService.getCountiesByRunConfigurationId(projectId.toString(), projectRunConfigurationId.toString(), true, true),
        projectService
          .getAvailableStatuses(Number(projectId), Number(projectRunConfigurationId))
          // Intentional soft-fail: if the status endpoint errors, suppress the County Status pill bar
          // and let the rest of the Service Area dropdown continue to work. The pills are an enhancement
          // — counties are still selectable, search still works — so a toast isn't warranted here.
          .catch(() => [] as AvailableRegionStatusesModel[]),
      ]);
      if (cancelled) return;

      if (specialties?.length > 0) {
        const sortedGroups = specialties
          .map(group => ({ ...group, specialties: [...(group.specialties ?? [])].sort((a, b) => specialtyLabel(a).localeCompare(specialtyLabel(b))) }))
          .sort((a, b) => specialtyLabel(a).localeCompare(specialtyLabel(b)));
        setSpecialtyGroups(sortedGroups);
      }

      let processedCounties = counties;
      if (counties && counties.length > 0) {
        // The API returns key = full name ("Missouri"), name = abbreviation ("MO").
        processedCounties = counties
          .map(parent => ({
            ...parent,
            children: [...parent.children].sort((a, b) => a.name.localeCompare(b.name)),
            key: parent.name,  // abbreviation ("MO") — used for internal state matching
            name: parent.key,  // full name ("Missouri") — used for display
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setServiceAreaOptions(processedCounties);
      }

      // Build the county-status → canonical-region-set-id map for the County Status pill filter.
      // Keying via county SSA (regionCode) matches PR 224846's partial-county fix in Provider Hub.
      const ssaToCanonicalRegionSetId = new Map<string, number>();
      processedCounties?.forEach(state => {
        state.children?.forEach(county => {
          if (county.countySSA) ssaToCanonicalRegionSetId.set(county.countySSA, Number(county.key));
        });
      });
      const newStatusToRegionSet = new Map<RegionSetStatusType, Set<number>>();
      availableStatuses?.forEach(s => {
        if (s.regionCode != null && s.availableStatuses?.length > 0) {
          const canonicalRegionSetId = ssaToCanonicalRegionSetId.get(s.regionCode);
          if (canonicalRegionSetId != null) {
            const status = s.availableStatuses[0];
            if (!newStatusToRegionSet.has(status)) {
              newStatusToRegionSet.set(status, new Set<number>());
            }
            newStatusToRegionSet.get(status).add(canonicalRegionSetId);
          }
        }
      });
      setStatusToRegionSet(newStatusToRegionSet);
      // Prefer the user's previously applied chip selection (persisted at module level so it
      // survives navigation between dashboard and detail pages). Fall back to the WI 189072
      // default — Active/Pending/Approved. Narrow to statuses actually present in this project.
      const sourceStatusFilters = getLastAppliedStatusFilters() ?? NI_DEFAULT_STATUS_FILTERS;
      const narrowedDefaultPills = new Set(sourceStatusFilters.filter(s => newStatusToRegionSet.has(s)));
      setActiveServiceAreaStatusFilters(narrowedDefaultPills);
      appliedServiceAreaStatusFilters.current = narrowedDefaultPills;
      const defaultPillExpansion = Array.from(narrowedDefaultPills)
        .flatMap(s => Array.from(newStatusToRegionSet.get(s) ?? []));

      const computedProviderType = routeProviderType ?? networkIntegrityContext.providerType;
      setPendingProviderType(computedProviderType);
      appliedProviderType.current = computedProviderType;
      setLastAppliedProviderType(computedProviderType);
      // Hydrate pending state from the applied context. Apply dispatches the *effective* regionSetIds
      // (the pill-expanded union when the user has no explicit county selection), so applyFiltersToContext
      // collapses an exact match back to [] to keep the implicit expansion from surfacing as a visible
      // Select All on the next page — see WI 189072.
      setPendingRegionSetIds(networkIntegrityContext.regionSetIds ?? []);
      setPendingSpecialtyCodes(specialtyCodesToSelectedKeys(specialties ?? [], networkIntegrityContext.specialtyCodes ?? []));
      setPendingActionStatus(networkIntegrityContext.actionStatus ?? NetworkIntegrityActionStatus.All);
      setPendingNpiType(networkIntegrityContext.npiType ?? null);
      setPendingMembership(computedMembership);
      setPendingResultsAsOfId(timesliceOptionId);
      setLastAppliedMembership(computedMembership);
      networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.MembershipFilterChanged, value: { membership: computedMembership } });

      initializeFilters(processedCounties, specialties ?? [], defaultPillExpansion);
      setIsLoading(false);
    })().finally(() => {
      // Let the widgets load even if setup throws. The filters stay hidden: their pending state was
      // never hydrated, so Apply would overwrite the applied selection.
      if (cancelled) return;
      networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.IsLodingChanged, value: { isLoading: false } });
    });

    return () => { cancelled = true; };
  }, [projectId, appliedResultsAsOfId]);

  useEffect(() => {
    const isPrimaryRunConfiguration = Number(networkIntegrityContext.projectRunConfigurationId) == primaryRunConfiguration?.projectRunConfigurationId
    setIsDualPrimaryMembershipSelected(isPrimaryRunConfiguration);

    // Back/Forward on the Provider Detail and Excess Warnings pages moves the run configuration through the URL, so mirror it into Membership.
    if (!(isProviderDetailPage || isExcessWarningsBreakdownPage) || isLoading || !hasDualMembershipProviders) return;
    const isCompanionRunConfiguration = Number(networkIntegrityContext.projectRunConfigurationId) === companionRunConfiguration?.projectRunConfigurationId;
    if (!isPrimaryRunConfiguration && !isCompanionRunConfiguration) return;

    const membership = isCompanionRunConfiguration ? NetworkIntegrityMembership.Secondary : NetworkIntegrityMembership.Primary;
    if (membership === appliedMembership) return;

    setPendingMembership(membership);
    setLastAppliedMembership(membership);
    networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.MembershipFilterChanged, value: { membership } });
    networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationChanged, value: { projectRunConfiguration: isCompanionRunConfiguration ? companionRunConfiguration : primaryRunConfiguration } });
    networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationDualTypeChanged, value: { projectRunConfigurationDualType: isCompanionRunConfiguration ? ProjectRunConfigurationType.DualMembershipSupplemental : ProjectRunConfigurationType.DualMembershipPrimary } });
  }, [networkIntegrityContext.projectRunConfigurationId]);

  // Back/Forward on the Provider Detail and Excess Warnings pages changes the provider type through the URL, so mirror it into the View dropdown.
  useEffect(() => {
    const providerType = networkIntegrityContext.providerType;
    if (isLoading || !providerType || providerType === appliedProviderType.current) return;

    appliedProviderType.current = providerType;
    setPendingProviderType(providerType);
    setLastAppliedProviderType(providerType);
  }, [networkIntegrityContext.providerType, isLoading]);

  const initializeFilters = (
    counties: ServiceAreaNestingParentDropdownItem[],
    specialties: SpecialtyGroupWithSpecialtiesModel[],
    defaultPillExpansion: number[]
  ): void => {
    const applyFiltersToContext = (regionSetIds: number[], specialtyCodes: string[]): void => {
      // When the user has no explicit county selection, the active County Status pills
      // implicitly constrain the dispatched regionSetIds (mirrors Provider Hub's behavior).
      // pendingRegionSetIds stays at the user's explicit selection (possibly []).
      const effectiveRegionSetIds = regionSetIds.length > 0 ? regionSetIds : defaultPillExpansion;
      // If the incoming list exactly matches the chip-derived expansion, the user had no
      // explicit selection on the prior page — collapse back to [] so navigating between
      // pages doesn't promote the implicit expansion into a visible Select All state.
      const resolvedPendingRegionSetIds = arraysEqual(regionSetIds, defaultPillExpansion) ? [] : regionSetIds;
      if (effectiveRegionSetIds.length > 0) {
        networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ServiceAreaFilterChanged, value: { regionSetIds: effectiveRegionSetIds } });
        setPendingRegionSetIds(resolvedPendingRegionSetIds);
      }
      if (specialtyCodes.length > 0) {
        networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.SpecialtyFilterChanged, value: { specialtyCodes } });
        setPendingSpecialtyCodes(specialtyCodesToSelectedKeys(specialties, specialtyCodes));
      }
    };

    if (isProviderDetailPage || isGapDetailPage || isViewAllPage) {
      // On detail pages, initialize from route state (carried forward from dashboard)
      const initialRegionSetIds = location.state?.initialRegionSetIds ?? [];
      const initialSpecialtyCodes = location.state?.initialSpecialtyCodes ?? [];
      applyFiltersToContext(initialRegionSetIds, initialSpecialtyCodes);
    } else {
      // Check if returning from a detail page with filters
      const returnedRegionSetIds = location.state?.initialRegionSetIds ?? [];
      const returnedSpecialtyCodes = location.state?.initialSpecialtyCodes ?? [];
      const hasReturnedFilters = returnedRegionSetIds.length > 0 || returnedSpecialtyCodes.length > 0;

      if (hasReturnedFilters) {
        applyFiltersToContext(returnedRegionSetIds, returnedSpecialtyCodes);

        // Map the detail page's NI filters back to Adequacy-compatible RegionFilters.
        // We must use the local `counties` parameter here — `serviceAreaOptions` state
        // hasn't been applied yet (React batches state updates).
        const returnFilters: RegionFilters = {};

        const returnedAdequacySpecialty = tryGetSingleFullyCoveredGroupCode(specialties, returnedSpecialtyCodes);
        if (returnedAdequacySpecialty) {
          returnFilters.specialty = returnedAdequacySpecialty;
        }

        if (returnedRegionSetIds.length > 0 && counties?.length > 0) {
          const matchingParents = counties.filter(parent =>
            parent.children.some(c => returnedRegionSetIds.includes(Number(c.key)))
          );
          if (matchingParents.length === 1) {
            const parent = matchingParents[0];
            const parentChildKeys = parent.children.map(c => Number(c.key));
            const allChildrenSelected = parentChildKeys.length === returnedRegionSetIds.length
              && parentChildKeys.every(k => returnedRegionSetIds.includes(k));

            if (returnedRegionSetIds.length === 1) {
              returnFilters.state = parent.key;
              const child = parent.children.find(c => Number(c.key) === returnedRegionSetIds[0]);
              if (child?.countySSA) returnFilters.county = child.countySSA;
            } else if (allChildrenSelected) {
              returnFilters.state = parent.key;
            }
          }
        }

        originalRegionSetIds.current = returnedRegionSetIds;
        originalSpecialtyCodes.current = returnedSpecialtyCodes;
        replaceLocationState({ returnRegionFilters: returnFilters });
      } else {
        // Sync Adequacy tab filters into NI filters before setting loading to false,
        // so widgets fetch exactly once with the correct filters applied.
        const regionFilters = projectContext?.regionFilters;

        const decodedSpecialty = regionFilters?.specialty
          ? decodeURIComponent(regionFilters.specialty)
          : regionFilters?.specialty;
        const matchedGroup = decodedSpecialty ? specialties?.find(s => s.code === decodedSpecialty) : null;
        const specialtyCodes: string[] = matchedGroup
          ? (matchedGroup.specialties ?? []).map(s => s.code)
          : [];

        let regionSetIds: number[] = [];
        if (regionFilters?.county && counties?.length > 0) {
          const child = counties.flatMap(p => p.children).find(c => c.countySSA === regionFilters.county);
          if (child) regionSetIds = [Number(child.key)];
        } else if (regionFilters?.state && counties?.length > 0) {
          const parent = counties.find(p => p.key === regionFilters.state);
          if (parent) regionSetIds = parent.children.map(c => Number(c.key));
        }

        applyFiltersToContext(regionSetIds, specialtyCodes);

        // Store the synced filter values as originals for change detection on return navigation
        originalRegionSetIds.current = regionSetIds;
        originalSpecialtyCodes.current = specialtyCodes;
        replaceLocationState({ returnRegionFilters: projectContext?.regionFilters ?? {} });
      }
    }
  };

  const arraysEqual = <T,>(a: T[], b: T[]): boolean => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((val, i) => val === sortedB[i]);
  };

  const serviceAreaStatusFilters = useMemo<{ key: string; displayValue: string }[]>(() => {
    return NI_STATUS_DISPLAY_ORDER
      .filter(s => (statusToRegionSet.get(s)?.size ?? 0) > 0)
      .map(s => ({ displayValue: s, key: s }));
  }, [statusToRegionSet]);

  const serviceAreaMatchFilter = useCallback(
    (child: DropdownItem<string>, activeKeys: string[]): boolean => {
      const childRegionSetId = Number(child.key);
      return activeKeys.some(key => statusToRegionSet.get(key as RegionSetStatusType)?.has(childRegionSetId) ?? false);
    },
    [statusToRegionSet]
  );

  const effectiveRegionSetIds = useMemo<number[]>(() => {
    if (pendingRegionSetIds.length > 0) return pendingRegionSetIds;
    return Array.from(activeServiceAreaStatusFilters)
      .flatMap(s => Array.from(statusToRegionSet.get(s) ?? []));
  }, [pendingRegionSetIds, activeServiceAreaStatusFilters, statusToRegionSet]);

  const nonServicingProviderType = pendingProviderType === NetworkIntegrityProviderTypes.Network || pendingProviderType === NetworkIntegrityProviderTypes.Prospect;
  const providerTypeChanged = (pendingProviderType !== appliedProviderType.current);
  const regionSetIdsChanged = !arraysEqual(effectiveRegionSetIds, networkIntegrityContext.regionSetIds ?? []);
  const specialtyChanged = !arraysEqual([...new Set(selectedKeysToSpecialtyCodes(pendingSpecialtyCodes))], networkIntegrityContext.specialtyCodes ?? []);
  const actionStatusChanged = pendingActionStatus !== (networkIntegrityContext.actionStatus ?? NetworkIntegrityActionStatus.All);
  const npiTypeChanged = showNpiTypeFilter && pendingNpiType !== (networkIntegrityContext.npiType ?? null);
  const statusFiltersChanged = !setsEqual(activeServiceAreaStatusFilters, appliedServiceAreaStatusFilters.current);
  const effectivePendingMembership = nonServicingProviderType 
    ? NetworkIntegrityMembership.Primary 
    : pendingMembership;
  const membershipChanged = hasDualMembershipProviders && effectivePendingMembership !== appliedMembership;
  const appliedResultsAsOfOrCurrent = appliedResultsAsOfId ?? primaryRunConfiguration?.projectRunConfigurationId?.toString();
  const resultsAsOfChanged = isDashboard && !!pendingResultsAsOfId && pendingResultsAsOfId !== appliedResultsAsOfOrCurrent;
  const hasUnappliedChanges = providerTypeChanged || regionSetIdsChanged || specialtyChanged || actionStatusChanged || npiTypeChanged || statusFiltersChanged || membershipChanged || resultsAsOfChanged;

  const allOptionsText = "All";

  const getServiceAreaSelectionText = (): string => {
    if (pendingRegionSetIds.length === 0) return allOptionsText;
    return getSelectionText(pendingRegionSetIds, serviceAreaOptions.flatMap(sa => sa.children));
  };

  const getSpecialtySelectionText = (): string => {
    if (pendingSpecialtyCodes.length === 0) return allOptionsText;
    return getSelectionText(pendingSpecialtyCodes, specialtyOptions.flatMap(g => g.children));
  };

  const handleServiceAreaChanged = (selectedItems: string[]): void => {
    setPendingRegionSetIds(selectedItems.map(Number));
  };

  const handleSpecialtyChanged = (selectedKeys: string[]): void => {
    setPendingSpecialtyCodes(selectedKeys);
  };

  const handleProviderTypeChanged = (value: string): void => {
    setPendingProviderType(value);
  };

  const handleDisplayModeChanged = (mode: NetworkIntegrityDisplayMode): void => {
    setLastAppliedDisplayMode(mode);
    networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.DisplayModeChanged, value: { displayMode: mode } });
  };

  const applyProviderTypeToContext = (value: string): void => {
    networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProviderTypeFilterChanged, value: { providerType: value } });

    // Reset job state in the same synchronous batch as the provider type change so that widgets
    // do not read a stale Finished status from the previous provider type when their effects fire.
    networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.IsJobLoadingChanged, value: { isJobLoading: true } });
    networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.JobStatusChanged, value: { jobStatus: undefined } });
  };

  const applyMembershipToContext = (membership: NetworkIntegrityMembership): void => {
    const isSecondary = membership === NetworkIntegrityMembership.Secondary;
    const targetConfig = isSecondary ? companionRunConfiguration : primaryRunConfiguration;
    if (!targetConfig) return;

    setIsDualPrimaryMembershipSelected(!isSecondary);

    if (networkIntegrityContext.projectRunConfigurationId !== targetConfig.projectRunConfigurationId.toString()) {
      networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationChanged, value: { projectRunConfiguration: targetConfig } });
      networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationIdChanged, value: { projectRunConfigurationId: targetConfig.projectRunConfigurationId.toString() } });
      networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationDualTypeChanged, value: { projectRunConfigurationDualType: isSecondary ? ProjectRunConfigurationType.DualMembershipSupplemental : ProjectRunConfigurationType.DualMembershipPrimary } });

      networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.IsJobLoadingChanged, value: { isJobLoading: true } });
      networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.JobStatusChanged, value: { jobStatus: undefined } });
    }
  };

  // Repoints the whole project, not just Network Integrity: CountyProjectOverview's tab gates and the 207375
  // module fix-up chain read the project-level run configuration, so letting the two diverge bounces the user out.
  const applyResultsAsOfToContext = (runConfigurationId: string): void => {
    const target = networkIntegrityContext.project?.projectRunConfigurations
      ?.find((rc) => rc.projectRunConfigurationId === Number(runConfigurationId));
    if (!target) return;

    setAppliedResultsAsOfId(runConfigurationId);

    replaceLocationState({ runConfiguration: target });

    networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationIdChanged, value: { projectRunConfigurationId: runConfigurationId } });
    networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationChanged, value: { projectRunConfiguration: target } });

    // Widgets gate on jobStatus === Finished and isJobStatusLoading === false; without this they would fetch the
    // new run while still holding the previous run's Finished status.
    networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.IsJobLoadingChanged, value: { isJobLoading: true } });
    networkIntegrityContext.jobStatusChanged({ type: NetworkIntegrityJobAction.JobStatusChanged, value: { jobStatus: undefined } });

    // Do not pass delayUpdate: setRunConfiguration only applies its state when delayUpdate is falsy.
    if (projectContext?.setRunConfiguration) {
      projectContext.setRunConfiguration(Number(runConfigurationId), moment.utc(target.dateCreated), false);
    }
    else if (zipProjectContext?.onChangeRunConfiguration) {
      zipProjectContext.onChangeRunConfiguration(Number(runConfigurationId));
    }
  };

  const computeReturnFilters = (appliedRegionSetIds: number[], appliedSpecialtyKeys: string[]): RegionFilters => {
    const appliedSpecialtyCodes = [...new Set(selectedKeysToSpecialtyCodes(appliedSpecialtyKeys))];
    const regionChanged = !arraysEqual(appliedRegionSetIds, originalRegionSetIds.current);
    const specialtyChanged = !arraysEqual(appliedSpecialtyCodes, originalSpecialtyCodes.current);

    if (!regionChanged && !specialtyChanged) {
      return projectContext?.regionFilters ?? {};
    }

    // Adequacy's specialty filter is a single group code. Map back only when the selection
    // is exactly one fully-selected group (all children of that group selected, nothing else).
    const adequacySpecialty = tryGetSingleFullyCoveredGroupCodeFromKeys(specialtyGroups, appliedSpecialtyKeys);
    if (appliedSpecialtyKeys.length > 0 && adequacySpecialty == null) return {};

    // Service area: determine state/county mapping
    if (appliedRegionSetIds.length > 0) {
      const matchingParents = serviceAreaOptions.filter(parent =>
        parent.children.some(c => appliedRegionSetIds.includes(Number(c.key)))
      );
      if (matchingParents.length !== 1) return {};

      const parent = matchingParents[0];
      const parentChildKeys = parent.children.map(c => Number(c.key));
      const allChildrenSelected = parentChildKeys.length === appliedRegionSetIds.length
        && parentChildKeys.every(k => appliedRegionSetIds.includes(k));

      if (appliedRegionSetIds.length === 1) {
        // Single county → state + county filter (even if it's the only county in the state)
        const child = parent.children.find(c => Number(c.key) === appliedRegionSetIds[0]);
        const filters: RegionFilters = { state: parent.key };
        if (child?.countySSA) filters.county = child.countySSA;
        if (adequacySpecialty) filters.specialty = adequacySpecialty;
        return filters;
      } else if (allChildrenSelected) {
        // Full state selected → state filter only
        const filters: RegionFilters = { state: parent.key };
        if (adequacySpecialty) filters.specialty = adequacySpecialty;
        return filters;
      } else {
        // Multiple counties but not all of the state → wipe
        return {};
      }
    }

    // No service area selections, just specialty
    const filters: RegionFilters = {};
    if (adequacySpecialty) filters.specialty = adequacySpecialty;
    return filters;
  };

  const handleApplyFilters = (): void => {
    // Applied first: it re-runs the load effect, which re-derives the membership pair, service areas and
    // specialties for the newly selected run.
    if (resultsAsOfChanged) {
      applyResultsAsOfToContext(pendingResultsAsOfId);
    }
    if (pendingProviderType !== appliedProviderType.current) {
      applyProviderTypeToContext(pendingProviderType);
    }
    appliedProviderType.current = pendingProviderType;
    setLastAppliedProviderType(pendingProviderType);
    networkIntegrityContext.filtersChanged({
      type: NetworkIntegrityFilterAction.ServiceAreaFilterChanged,
      value: { regionSetIds: effectiveRegionSetIds },
    });
    networkIntegrityContext.filtersChanged({
      type: NetworkIntegrityFilterAction.SpecialtyFilterChanged,
      value: { specialtyCodes: [...new Set(selectedKeysToSpecialtyCodes(pendingSpecialtyCodes))] },
    });
    networkIntegrityContext.filtersChanged({
      type: NetworkIntegrityFilterAction.ActionStatusFilterChanged,
      value: { actionStatus: pendingActionStatus },
    });
    if (showNpiTypeFilter) {
      networkIntegrityContext.filtersChanged({
        type: NetworkIntegrityFilterAction.NpiTypeFilterChanged,
        value: { npiType: pendingNpiType },
      });
    }
    appliedServiceAreaStatusFilters.current = new Set(activeServiceAreaStatusFilters);
    setLastAppliedStatusFilters([...activeServiceAreaStatusFilters]);
    if (membershipChanged) {
      applyMembershipToContext(effectivePendingMembership);
    }
    setLastAppliedMembership(effectivePendingMembership);
    networkIntegrityContext.filtersChanged({
      type: NetworkIntegrityFilterAction.MembershipFilterChanged,
      value: { membership: effectivePendingMembership },
    });

    if (!isProviderDetailPage && !isGapDetailPage) {
      // Update return filters for Adequacy navigation
      const returnFilters = computeReturnFilters(pendingRegionSetIds, pendingSpecialtyCodes);
      replaceLocationState({ returnRegionFilters: returnFilters });
    }

    if (!isNetworkProviderType(pendingProviderType)) {
      networkIntegrityContext.filtersChanged({
        type: NetworkIntegrityFilterAction.DisplayModeChanged,
        value: { displayMode: NetworkIntegrityDisplayMode.Current }
      })
    }
  };

  const handleResetFilters = (): void => {
    if (hasUnappliedChanges) {
      // Discard pending changes — revert to currently applied values
      setPendingProviderType(appliedProviderType.current);
      setPendingRegionSetIds(networkIntegrityContext.regionSetIds ?? []);
      setPendingSpecialtyCodes(specialtyCodesToSelectedKeys(specialtyGroups, networkIntegrityContext.specialtyCodes ?? []));
      setPendingActionStatus(networkIntegrityContext.actionStatus ?? NetworkIntegrityActionStatus.All);
      setPendingNpiType(networkIntegrityContext.npiType ?? null);
      setActiveServiceAreaStatusFilters(new Set(appliedServiceAreaStatusFilters.current));
      setPendingMembership(appliedMembership);
      setPendingResultsAsOfId(appliedResultsAsOfOrCurrent);
    } else {
      // No pending changes — restore the default pill set, clear other applied filters,
      // and dispatch the pill-derived effective regionSetIds so widgets refetch against the default scope.
      const defaultPills = new Set(NI_DEFAULT_STATUS_FILTERS.filter(s => statusToRegionSet.has(s)));
      const defaultPillExpansion = Array.from(defaultPills)
        .flatMap(s => Array.from(statusToRegionSet.get(s) ?? []));
      setActiveServiceAreaStatusFilters(defaultPills);
      appliedServiceAreaStatusFilters.current = defaultPills;
      setLastAppliedStatusFilters(undefined);
      setPendingRegionSetIds([]);
      setPendingSpecialtyCodes([]);
      setPendingActionStatus(NetworkIntegrityActionStatus.All);
      setPendingNpiType(null);
      setPendingMembership(NetworkIntegrityMembership.Primary);
      setLastAppliedMembership(NetworkIntegrityMembership.Primary);
      // Reset All clears filters within the selected timeslice; it does not move the timeslice.
      setPendingResultsAsOfId(appliedResultsAsOfOrCurrent);
      // Repoint the run configuration back to primary too — otherwise resetting from a secondary
      // membership leaves the membership field on Primary while widgets keep fetching companion data.
      if (appliedMembership !== NetworkIntegrityMembership.Primary) {
        applyMembershipToContext(NetworkIntegrityMembership.Primary);
      }
      networkIntegrityContext.filtersChanged({
        type: NetworkIntegrityFilterAction.MembershipFilterChanged,
        value: { membership: NetworkIntegrityMembership.Primary },
      });
      // Reset the View to the Servicing default. Guard the context dispatch so an already-default
      // View doesn't needlessly repoint run config / reset job state.
      setPendingProviderType(NI_DEFAULT_PROVIDER_TYPE);
      setLastAppliedProviderType(NI_DEFAULT_PROVIDER_TYPE);
      if (appliedProviderType.current !== NI_DEFAULT_PROVIDER_TYPE) {
        applyProviderTypeToContext(NI_DEFAULT_PROVIDER_TYPE);
      }
      appliedProviderType.current = NI_DEFAULT_PROVIDER_TYPE;
      networkIntegrityContext.filtersChanged({
        type: NetworkIntegrityFilterAction.ServiceAreaFilterChanged,
        value: { regionSetIds: defaultPillExpansion },
      });
      networkIntegrityContext.filtersChanged({
        type: NetworkIntegrityFilterAction.SpecialtyFilterChanged,
        value: { specialtyCodes: [] },
      });
      networkIntegrityContext.filtersChanged({
        type: NetworkIntegrityFilterAction.ActionStatusFilterChanged,
        value: { actionStatus: NetworkIntegrityActionStatus.All },
      });
      networkIntegrityContext.filtersChanged({
        type: NetworkIntegrityFilterAction.NpiTypeFilterChanged,
        value: { npiType: null },
      });

      if (!isProviderDetailPage) {
        const returnFilters = computeReturnFilters([], []);
        replaceLocationState({ returnRegionFilters: returnFilters });
      }
    }
  };

  const hasActiveActionStatus = networkIntegrityContext.actionStatus
    && networkIntegrityContext.actionStatus !== NetworkIntegrityActionStatus.All;
  const hasActiveNpiType = showNpiTypeFilter && !!networkIntegrityContext.npiType;
  const hasNonDefaultMembership = hasDualMembershipProviders && appliedMembership !== NetworkIntegrityMembership.Primary;
  // A non-default applied View keeps Reset All enabled, like a non-empty regionSetIds/specialtyCodes.
  // Context-backed so it recomputes on the Apply/Reset render.
  const hasNonDefaultProviderType = (networkIntegrityContext.providerType ?? NI_DEFAULT_PROVIDER_TYPE) !== NI_DEFAULT_PROVIDER_TYPE;
  const hasActiveFilters = hasUnappliedChanges
    || (networkIntegrityContext.regionSetIds?.length > 0 || networkIntegrityContext.specialtyCodes?.length > 0 || hasActiveActionStatus || hasActiveNpiType || hasNonDefaultMembership || hasNonDefaultProviderType);

  const truncateIfTooLong = (alias: string, maxLength: number): string => {
    if (!alias || alias.length <= maxLength) return alias;
    return alias.substring(0, maxLength - 1) + "…";
  }

  return (!isLoading &&
    <div className={`ni-filters__container${(isProviderDetailPage || isGapDetailPage || isViewAllPage) ? ' px-0' : ''}`}>
      <div className="d-flex flex-wrap align-items-center ni-filters__row">
        {isDashboard && <NetworkIntegrityResultsAsOfFilter
          options={resultsAsOfOptions}
          selectedId={pendingResultsAsOfId}
          setSelectedId={setPendingResultsAsOfId}
        />}
        <NetworkIntegrityProviderTypeFilter
          providerType={pendingProviderType}
          setProviderType={handleProviderTypeChanged}
          hasProspectProviders={hasProspectProviders}
          onlyServicingProviders={isGapDetailPage}
        />
        {hasDualMembershipProviders && <NetworkIntegrityMembershipFilter
          membership={effectivePendingMembership}
          setMembership={setPendingMembership}
          primaryMembershipAlias={primaryMemberAlias}
          secondaryMembershipAlias={secondaryMemberAlias}
          disabled={nonServicingProviderType}
        />}
        <NetworkIntegrityServiceAreaFilter
          filters={serviceAreaStatusFilters}
          items={serviceAreaOptions}
          matchFilter={serviceAreaMatchFilter}
          onSelectedFiltersChange={(keys) =>
            setActiveServiceAreaStatusFilters(new Set(keys as RegionSetStatusType[]))}
          selectedFilters={[...activeServiceAreaStatusFilters]}
          selectedItems={serviceAreaOptions
            .flatMap(sa => sa.children)
            .filter(c => pendingRegionSetIds.includes(Number(c.key)))
            .map(c => c.key)}
          selectionText={getServiceAreaSelectionText()}
          onSelectionChanged={handleServiceAreaChanged}
        />
        <NetworkIntegritySpecialtyFilter
          items={specialtyOptions}
          selectedItems={specialtySelectedKeys}
          selectionText={getSpecialtySelectionText()}
          onSelectionChanged={handleSpecialtyChanged}
        />
        {showActionStatusFilter && <NetworkIntegrityActionStatusFilter
          actionStatus={pendingActionStatus}
          setActionStatus={setPendingActionStatus}
        />}
        {showNpiTypeFilter && <NetworkIntegrityNpiTypeFilter
          npiType={pendingNpiType}
          setNpiType={setPendingNpiType}
        />}
        <QESButton
          className="ni-filters__apply-button"
          qesType="primary"
          qesTestId="network-integrity__apply-filters-button"
          disabled={!hasUnappliedChanges}
          onClick={handleApplyFilters}>
          Apply
        </QESButton>
        <QESButton
          className="ni-filters__reset-button"
          qesType="secondary"
          qesTestId="network-integrity__reset-filters-button"
          disabled={!hasActiveFilters}
          onClick={handleResetFilters}>
          Reset All
        </QESButton>
      </div>
      {displayToggleEligible && hasProcessedTerminations && (
        <div className="ni-filters__display-row">
          <span className="ni-filters__display-label">Display: </span>
          <div className="ni-filters__display-toggle">
            <button
              className={`ni-filters__seg-btn${networkIntegrityContext.displayMode === NetworkIntegrityDisplayMode.Current ? ' ni-filters__seg-btn--active' : ''}`}
              data-testid="network-integrity__display-current-button"
              onClick={() => handleDisplayModeChanged(NetworkIntegrityDisplayMode.Current)}
              type="button"
            >
              Current
            </button>
            <button
              className={`ni-filters__seg-btn${networkIntegrityContext.displayMode === NetworkIntegrityDisplayMode.TotalAfterTermed ? ' ni-filters__seg-btn--active' : ''}`}
              data-testid="network-integrity__display-total-after-termed-button"
              onClick={() => handleDisplayModeChanged(NetworkIntegrityDisplayMode.TotalAfterTermed)}
              type="button"
            >
              Total After Termed
            </button>
          </div>
          {networkIntegrityContext.displayMode === NetworkIntegrityDisplayMode.TotalAfterTermed && (
            <InfoIndicator
              className="ni-filters__display-info"
              tooltipPlacement="bottom"
              tooltipTitle={
                <>
                  <span style={{ display: 'block', textAlign: 'left' }}>
                    Total After Termed reflects the updated counts once taking into account{" "}
                    <strong>processed</strong> terminations from any workflow and with any termination reason.
                    Terminations that are unprocessed and still pending are not reflected in the analysis.
                  </span>
                </>
              }
              tooltipWidth="320px"
            ></InfoIndicator>
          )}
        </div>
      )}
      <NetworkIntegrityAppliedFilters
        pendingActionStatus={pendingActionStatus}
        pendingProviderType={pendingProviderType}
        pendingRegionSetIds={pendingRegionSetIds}
        pendingSpecialtyCodes={pendingSpecialtyCodes}
        pendingMembership={nonServicingProviderType ? null : pendingMembership}
        pendingNpiType={showNpiTypeFilter ? pendingNpiType : null}
        setPendingActionStatus={setPendingActionStatus}
        setPendingNpiType={setPendingNpiType}
        setPendingRegionSetIds={setPendingRegionSetIds}
        setPendingSpecialtyCodes={setPendingSpecialtyCodes}
        serviceAreaOptions={serviceAreaOptions}
        specialtyGroups={specialtyGroups}
        statusToRegionSet={statusToRegionSet}
        hasDualMembershipProviders={hasDualMembershipProviders}
        isDualPrimaryMembershipSelected={isDualPrimaryMembershipSelected}
        primaryMembershipAlias={primaryMemberAlias}
        secondaryMembershipAlias={secondaryMemberAlias}
        excessWarning={excessWarning.current}
      />
    </div>
  );
}

export default NetworkIntegrityFilterContainer;